import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Center,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { dinero, isZero } from 'dinero.js';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as queryString from 'query-string';
import * as React from 'react';

import { CurrencyDisplay } from '@/components/Currency';
import { EventTime } from '@/components/EventTime';
import { TicketOrderDetailsCard } from '@/components/TicketOrderDetailsCard';
import { EAccountCreationPermission } from '@/graphql-types/globalTypes';
import { CartItemType } from '@/graphql-types/globalTypes';
import { usePermissions } from '@/hooks/usePermissions';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { Semibold } from '@/imports/ui/chakra/components/Semibold';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { InfoIcon, ShoppingCartIcon } from '@/imports/ui/chakra/feather';

import {
  EventRegistrationModal_PurchaseFreeEventTicket,
  EventRegistrationModal_PurchaseFreeEventTicketVariables,
} from './graphql-types/EventRegistrationModal_PurchaseFreeEventTicket';
import {
  EventRegistrationModal_Query,
  EventRegistrationModal_QueryVariables,
} from './graphql-types/EventRegistrationModal_Query';

import { TicketSelect } from './components/TicketSelect';

const PURCHASE_FREE_EVENT_TICKET = gql`
  mutation EventRegistrationModal_PurchaseFreeEventTicket(
    $input: PurchaseFreeEventTicketInput!
  ) {
    purchaseFreeEventTicket(input: $input) {
      registration {
        _id
      }
    }
  }
`;

const QUERY = gql`
  query EventRegistrationModal_Query($input: EventInput!) {
    event(input: $input) {
      eventV2 {
        _id
        ticketsCurrentUserCanPurchase {
          _id
          name
          description
          price
          canPurchase {
            ... on Ticket_CanPurchase_MagicLink {
              type
            }
            ... on Ticket_CanPurchase_PermissionConfig {
              type
            }
          }
          requiresAttributeReview
        }
        currentValidUserRegistration {
          _id
          ticketId
          ticket {
            _id
          }
        }
        ...TicketSelect_EventFragment
      }
    }
    tenant {
      result {
        _id
        accountCreationPermission
      }
    }
  }
  ${TicketSelect.eventFragment}
`;

export const EventRegistrationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  eventName: string;
  eventStartDate: string;
  eventEndDate: string;
  isConference: boolean;
}> = (props) => {
  const permissions = usePermissions();
  const router = useRouter();
  const toast = useToast();
  const query = useQuery<
    EventRegistrationModal_Query,
    EventRegistrationModal_QueryVariables
  >(QUERY, {
    variables: { input: { _id: props.eventId } },
    skip: !props.isOpen,
  });

  const event = query.data?.event.eventV2;

  const canSignUp =
    query.data?.tenant.result?.accountCreationPermission ===
    EAccountCreationPermission.ANYONE;

  const ticketLinkId = router.query.ticketLinkId
    ? router.query.ticketLinkId.toString()
    : undefined;

  const preSelectedTicketId = router.query.preSelectedTicketId
    ? router.query.preSelectedTicketId.toString()
    : undefined;

  const [selectedTicketId, setSelectedTicketId] = React.useState<
    string | undefined
  >(preSelectedTicketId);

  // Convert single ticket ID to array format for TicketSelect compatibility
  const selectedTickets = React.useMemo(() => {
    if (!selectedTicketId || !event) return [];
    const ticket = event.ticketsCurrentUserCanPurchase.find(
      (t) => t._id === selectedTicketId
    );
    if (!ticket) return [];
    return [
      {
        ...ticket,
        eventTicketId: ticket._id,
        itemId: ticket._id,
        itemType: CartItemType.EVENTS,
        eventId: props.eventId,
        quantity: 1,
      },
    ];
  }, [selectedTicketId, event, props.eventId]);

  const handleTicketsChange = React.useCallback(
    (tickets: import('./interface').IEventItemsForCart[]) => {
      // Find the first ticket with quantity > 0
      const selectedTicket = tickets.find((t) => t.quantity > 0);
      setSelectedTicketId(selectedTicket?.itemId || undefined);
    },
    []
  );

  const selectedTicket = event?.ticketsCurrentUserCanPurchase.find(
    (ticket) => ticket._id === selectedTicketId
  );

  const isFree = selectedTicket ? isZero(dinero(selectedTicket.price)) : false;

  const requiresAttributeReview = selectedTicket
    ? selectedTicket.requiresAttributeReview
    : false;

  const [isLoading, setIsLoading] = React.useState(false);

  const [purchaseFreeEventTicket] = useMutation<
    EventRegistrationModal_PurchaseFreeEventTicket,
    EventRegistrationModal_PurchaseFreeEventTicketVariables
  >(PURCHASE_FREE_EVENT_TICKET);

  const onSubmit = async () => {
    if (!event) {
      throw new Error('Unexpected Missing Event');
    }

    if (!selectedTicket) {
      throw new Error('Unexpected Missing Ticket');
    }

    setIsLoading(true);

    try {
      await purchaseFreeEventTicket({
        variables: { input: { ticketId: selectedTicket._id } },
        onError: () => setIsLoading(false),
      });

      router.push(`/events/${event._id}/registration`);
    } catch {
      toast({ status: 'error', description: 'An Unexpected Error Occurred' });
    }
  };

  const checkoutPageUrl = `/events/${
    props.eventId
  }/tickets/${selectedTicketId}/buy?${queryString.stringify({
    ...(ticketLinkId ? { ticketLinkId } : undefined),
  })}`;

  const postLoginRedirectUrl = React.useMemo(() => {
    if (requiresAttributeReview) {
      return checkoutPageUrl;
    }

    if (isFree) {
      if (props.isConference) {
        return `/events/conferences/${props.eventId}?${queryString.stringify({
          ...(ticketLinkId ? { ticketLinkId } : undefined),
          ...(selectedTicketId
            ? { preSelectedTicketId: selectedTicketId }
            : undefined),
        })}`;
      }

      return `/events/${props.eventId}?${queryString.stringify({
        ...(ticketLinkId ? { ticketLinkId } : undefined),
        ...(selectedTicketId
          ? { preSelectedTicketId: selectedTicketId }
          : undefined),
      })}`;
    }

    return checkoutPageUrl;
  }, [
    checkoutPageUrl,
    isFree,
    props.eventId,
    props.isConference,
    requiresAttributeReview,
    selectedTicketId,
    ticketLinkId,
  ]);

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      size="4xl"
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader borderBottom="1px" borderBottomColor="#F5F5F5">
          <Stack spacing="0">
            <Heading size="md">{props.eventName}</Heading>
            <Text textStyle="compact" color="#333333" fontWeight="normal">
              <EventTime
                startDate={props.eventStartDate}
                endDate={props.eventEndDate}
              />
            </Text>
          </Stack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p="0">
          {query.loading ? (
            <Loading />
          ) : query.error || !event ? (
            <UnexpectedError />
          ) : (
            <Stack direction={{ base: 'column', md: 'row' }} spacing="0">
              <Box flex="1" px="6" py="4">
                <Text>Something went wrong..</Text>
                {/* <TicketSelect
                  value={selectedTickets}
                  onChange={handleTicketsChange}
                  event={event}
                  eventId={props.eventId}
                  isDisabled={isLoading}
                /> */}
              </Box>
              <Box
                bg="#F5F5F5"
                minWidth="xs"
                minHeight="lg"
                px="6"
                py="4"
                display={{ base: 'none', md: 'block' }}
              >
                {selectedTicket ? (
                  <TicketOrderDetailsCard
                    ticketName={selectedTicket.name}
                    ticketPrice={dinero(selectedTicket.price)}
                  />
                ) : (
                  <Center h="full">
                    <ShoppingCartIcon boxSize="8" color="#CCCCCC" />
                  </Center>
                )}
              </Box>
            </Stack>
          )}
        </ModalBody>
        <ModalFooter p="0" display="block">
          {selectedTicket && (
            <Stack
              bg="#F5F5F5"
              direction="row"
              justifyContent="space-between"
              px="6"
              py="4"
              borderTop="1px"
              borderTopColor="#F5F5F5"
              display={{ base: 'flex', md: 'none' }}
            >
              <Text textStyle="bodyStrong" color="#333333">
                Amount Due
              </Text>
              <CurrencyDisplay
                value={dinero(selectedTicket.price)}
                textStyle="bodyStrong"
              />
            </Stack>
          )}
          <Stack
            direction={{ base: 'column', md: 'row' }}
            px="6"
            py="4"
            borderTop="1px"
            borderTopColor="#F5F5F5"
            justifyContent="flex-end"
          >
            {!permissions.isAuthenticated && (
              <Stack direction="row" flex="1" alignItems="center">
                <InfoIcon />
                {canSignUp ? (
                  <Text>
                    You will need to <Semibold>Sign In</Semibold> or{' '}
                    <Semibold>Sign Up</Semibold> for a Tradewing account to
                    register
                  </Text>
                ) : (
                  <Text>
                    You will need to <Semibold>Sign In</Semibold> to register
                  </Text>
                )}
              </Stack>
            )}

            <Button
              variant="outline"
              colorScheme="gray"
              onClick={props.onClose}
              isDisabled={isLoading}
            >
              Cancel
            </Button>
            {!permissions.isAuthenticated ? (
              <NextLink
                href={`/login?${queryString.stringify({
                  isTicketFlow: true,
                  redirect: postLoginRedirectUrl,
                })}`}
              >
                <Button isDisabled={!selectedTicketId}>
                  Continue to Sign In
                </Button>
              </NextLink>
            ) : isFree && !requiresAttributeReview ? (
              <Button
                isLoading={isLoading}
                isDisabled={!selectedTicketId}
                onClick={onSubmit}
              >
                Submit
              </Button>
            ) : (
              <NextLink href={checkoutPageUrl}>
                <Button isDisabled={!selectedTicketId}>Checkout</Button>
              </NextLink>
            )}
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
